import axiosInstance from "../data/interface/axiosInstance"

export interface FarmData {
  id: number
  farmName: string
  address: string
  manager: string
  assistantManager: string
  mobileNumber: string
  officeNumber: string
  email: string
  farmPostalCode: string
}

export interface AuditType {
  inActive: boolean
  inActiveReason: string
  inActiveBy: string
  inActiveDate: string
  addedBy: string
  addedDate: string
  updatedBy: string
  updatedDate: string
  id: number
  auditTypeName: string
  description: string
}

export interface Audit {
  id: number
  auditName: string
  auditTypeId: number
  farmId: number
  teamId: number
  // Returned by GetAllAudits (it joins Teams), so it is present even when the
  // date-filtered team picker cannot offer that teamId as an option.
  teamName?: string
  // Returned by GetAllAudits: one entry per id in reportId, named even when the
  // report was deleted after the audit was scheduled (the report PICKER hides
  // soft-deleted rows, so the id alone cannot be resolved client-side).
  reports?: { id: number; name: string | null }[]
  reportId: string | any
  auditorId: string
  scheduledDate: string
  startDate: string
  endDate: string
  inActive: boolean
  inActiveReason: string
  inActiveBy: string
  inActiveDate: string
  addedBy: string
  addedDate: string
  updatedBy: string
  updatedDate: string
}

export interface ApiResponse<T> {
  isError: boolean
  responseMessage: string
  dataModel: T
}

class AuditService {
  async getAllFarms(): Promise<ApiResponse<FarmData[]>> {
    const response = await axiosInstance.get("/Farm/GetFarmsData")
    return response.data
  }

  async getAllAuditTypes(): Promise<ApiResponse<AuditType[]>> {
    const response = await axiosInstance.get("/Audit/GetAuditAllTypes")
    return response.data
  }

  async getAuditTypeById(id: number): Promise<ApiResponse<AuditType>> {
    const response = await axiosInstance.get(`/Audit/GetAuditTypeById?id=${id}`)
    return response.data
  }

  async saveAuditType(auditType: AuditType): Promise<ApiResponse<AuditType>> {
    const response = await axiosInstance.post("/Audit/AddUpdateAuditTypeData", auditType)
    return response.data
  }

  async updateAuditType(auditType: AuditType): Promise<ApiResponse<AuditType>> {
    const response = await axiosInstance.post(`/Audit/AddUpdateAuditTypeData`, auditType)
    return response.data
  }

  async deleteAuditType(id: number): Promise<ApiResponse<void>> {
    const response = await axiosInstance.delete(`/Audit/DeleteAuditTypeById?id=${id}`)
    return response.data
  }

  async getAllAudits(): Promise<ApiResponse<Audit[]>> {
    const response = await axiosInstance.get("/Audit/GetAllAudits")
    return response.data
  }

  // Audit-owned team list for a given order/scheduled date. Requires `audits.view` (the key an
  // audit-scheduler already holds), so scheduling no longer depends on `jobs.view` via Allotment.
  async getTeamsByOrderDate(orderDate: string): Promise<ApiResponse<any[]>> {
    const response = await axiosInstance.get(
      `/Audit/GetTeamsByOrderDate?orderDate=${encodeURIComponent(orderDate)}`
    )
    return response.data
  }

  async getAuditById(id: number): Promise<ApiResponse<Audit>> {
    const response = await axiosInstance.get(`/Audit/GetAuditById?id=${id}`)
    return response.data
  }

  async saveAudit(audit: Audit): Promise<ApiResponse<Audit>> {
    const response = await axiosInstance.post("/Audit/AddUpdateAuditData", audit)
    return response.data
  }

  async updateAudit(audit: Audit): Promise<ApiResponse<Audit>> {
    const response = await axiosInstance.post(`/Audit/AddUpdateAuditData`, audit)
    return response.data
  }

  async deleteAudit(id: number): Promise<ApiResponse<void>> {
    const response = await axiosInstance.delete(`/Audit/DeleteAuditById?id=${id}`)
    return response.data
  }
}

export const auditService = new AuditService()
